import React from "react";
import { Link } from "react-router-dom";
import { Clock, Activity, UserCheck, Database, ArrowRight } from "lucide-react";
import { formatDuration, formatNumber } from "../lib/format";

// Uptime band colour: green ≥95%, amber 80–95%, red <80%.
const uptimeColor = (pct) => {
  if (pct == null) return "var(--text-dim)";
  if (pct >= 95) return "var(--highlight)";
  if (pct >= 80) return "var(--secondary-accent)";
  return "var(--ping-high)";
};

const formatTrackedSince = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  const rel = days === 0 ? "today" : `${days}d ago`;
  return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} · ${rel}`;
};

const UptimeBar = ({ pct, label, testId }) => {
  const colour = uptimeColor(pct);
  const value = pct == null ? "—" : `${pct.toFixed(1)}%`;
  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      <div className="flex items-baseline justify-between">
        <span className="hud-label">{label}</span>
        <span
          className="font-mono-hud text-lg font-bold tabular-nums"
          style={{ color: colour }}
          data-testid={`${testId}-value`}
        >
          {value}
        </span>
      </div>
      <div
        className="relative h-1.5 w-full overflow-hidden"
        style={{ background: "var(--bg-elevated)", borderRadius: 2 }}
      >
        <div
          className="absolute inset-y-0 left-0 transition-all"
          style={{
            width: pct == null ? "0%" : `${Math.min(100, Math.max(0, pct))}%`,
            background: colour,
            boxShadow: `0 0 6px color-mix(in srgb, ${colour} 60%, transparent)`,
          }}
        />
      </div>
    </div>
  );
};

const StatBlock = ({ icon, label, value, sub, testId, valueColor }) => (
  <div
    className="flex items-start gap-3 border p-3"
    style={{ borderColor: "var(--border-color)", background: "var(--bg-main)", borderRadius: 2 }}
    data-testid={testId}
  >
    <span style={{ color: "var(--primary-accent)" }} className="mt-0.5">{icon}</span>
    <div className="min-w-0 flex-1">
      <div className="hud-label">{label}</div>
      <div
        className="font-mono-hud text-sm font-bold tabular-nums mt-0.5"
        style={{ color: valueColor || "var(--text-primary)" }}
      >
        {value}
      </div>
      {sub && (
        <div className="font-mono-hud text-[10px] mt-0.5" style={{ color: "var(--text-dim)" }}>
          {sub}
        </div>
      )}
    </div>
  </div>
);

export const ServerReliabilityPanel = ({ details }) => {
  if (!details) return null;
  const {
    server_id,
    uptime_7d_pct,
    uptime_30d_pct,
    match_duration_min,
    match_started_at,
    reserved_players,
    tracked_since,
    samples_count,
  } = details;

  const matchStartedLabel = match_started_at
    ? new Date(match_started_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div
      className="tac-card p-4"
      style={{ background: "var(--bg-surface)" }}
      data-testid="reliability-panel"
    >
      <span className="corner-tr" />
      <span className="corner-bl" />
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="hud-label">RELIABILITY & SESSION</div>
        {server_id && (
          <Link
            to={`/server/${encodeURIComponent(server_id)}/downtime`}
            className="font-mono-hud text-xs tracking-widest inline-flex items-center gap-1 hover:underline transition-opacity hover:opacity-80"
            style={{ color: "var(--primary-accent)" }}
            data-testid="downtime-history-link"
          >
            VIEW DOWNTIME HISTORY <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <UptimeBar pct={uptime_7d_pct} label="UPTIME · 7 DAYS" testId="uptime-7d" />
        <UptimeBar pct={uptime_30d_pct} label="UPTIME · 30 DAYS" testId="uptime-30d" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBlock
          icon={<Clock className="w-4 h-4" />}
          label="MATCH STARTED"
          value={match_duration_min != null ? formatDuration(match_duration_min) + " ago" : "—"}
          sub={matchStartedLabel ? `at ${matchStartedLabel} UTC` : null}
          testId="match-started"
        />
        <StatBlock
          icon={<UserCheck className="w-4 h-4" />}
          label="RESERVED SLOTS"
          value={reserved_players == null ? "—" : String(reserved_players)}
          sub={reserved_players ? "staff / VIP seats" : "none reserved"}
          testId="reserved-slots"
        />
        <StatBlock
          icon={<Database className="w-4 h-4" />}
          label="TRACKED SINCE"
          value={formatTrackedSince(tracked_since)}
          testId="tracked-since"
        />
        <StatBlock
          icon={<Activity className="w-4 h-4" />}
          label="SAMPLES"
          value={samples_count ? formatNumber(samples_count) : "—"}
          sub="minute-level snapshots"
          testId="samples-count"
        />
      </div>
    </div>
  );
};

export default ServerReliabilityPanel;
